// ────────────────────────────────────────────────────────────────────
// Recent desks — the "กลับไปที่โต๊ะเดิม" list on the staff landing.
//
// Stores asset IDS ONLY, never tags or names: nothing identifying persists in
// the browser, and the server re-reads every figure under RLS anyway. The key
// is tenant-scoped so switching tenant cannot surface the previous tenant's
// deck — and even if a stale id did survive, /api/desk/summary drops ids the
// session cannot see (see the RLS test).
// ────────────────────────────────────────────────────────────────────
import { useEffect } from "react";
import { getActiveTenant } from "@/lib/api";

const MAX = 5;

function key(): string | null {
  const t = getActiveTenant();
  return t ? `xzynexa.recent.${t}` : null;
}

/** NOTE (2026-08-04): no caller reads this today. The desk landing used to,
 *  and stopped when it became a doorway; the page itself retired on
 *  2026-09-01 (see App.tsx). The
 *  writer below is still live (AssetWorkspaceScreen), deliberately: it costs a
 *  handful of ints in localStorage and it means the "กลับไปที่โต๊ะเดิม" surface
 *  can come back with history intact rather than starting from empty. */
export function readRecentDesks(): number[] {
  const k = key();
  if (!k) return [];
  try {
    const raw = localStorage.getItem(k);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((n): n is number => typeof n === "number" && Number.isInteger(n))
                 .slice(0, MAX);
  } catch {
    // Corrupt storage is not worth breaking the landing page over — an older
    // build may have written a different shape here.
    return [];
  }
}

export function recordRecentDesk(assetId: number): void {
  const k = key();
  if (!k || !Number.isInteger(assetId)) return;
  const next = [assetId, ...readRecentDesks().filter((id) => id !== assetId)].slice(0, MAX);
  try {
    localStorage.setItem(k, JSON.stringify(next));
  } catch {
    // Quota or private-mode failure — recents are a convenience, never a
    // correctness requirement.
  }
}

/** Record the asset as the most recently opened desk, once per mount. */
export function useRecordRecentDesk(assetId: number | null | undefined): void {
  useEffect(() => {
    if (typeof assetId === "number") recordRecentDesk(assetId);
  }, [assetId]);
}
